import React from "react";
import { motion } from "framer-motion";

/**
 * Editorial section primitives.
 *
 * Every section on the site is composed from these three pieces so that
 * rhythm, tone alternation and headline voice stay identical everywhere.
 */

const TONES = {
  paper: "bg-paper text-ink",
  warm: "bg-paper-warm text-ink paper-grain",
  deep: "on-deep bg-paper-deep text-[color:var(--ink-invert)]",
};

/** Vertical rhythm tiers — sections never invent their own padding. */
const SPACE = {
  sm: "py-16 md:py-20",
  md: "py-20 md:py-28",
  lg: "py-24 md:py-36",
};

export const Section = ({
  id,
  tone = "paper",
  space = "lg",
  divider = true,
  className = "",
  children,
}) => (
  <section
    id={id}
    className={`relative w-full ${TONES[tone]} ${SPACE[space]} ${
      divider ? "border-t border-[color:var(--rule)]" : ""
    } ${className}`}
  >
    <div className="shell relative">{children}</div>
  </section>
);

/**
 * Section opener: hairline + index + label, serif headline, lede.
 * `aside` renders opposite the headline to break the centred-stack monotony.
 */
export const SectionHeader = ({
  index,
  label,
  title,
  lede,
  aside,
  align = "left",
  className = "",
}) => {
  const centered = align === "center";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      <div
        className={`flex items-baseline gap-3 border-t border-[color:var(--rule)] pt-3 ${
          centered ? "justify-center" : ""
        }`}
      >
        {index && (
          <span className="label tabular text-gold-ink">{index}</span>
        )}
        <span className="label">{label}</span>
      </div>

      <div
        className={`mt-7 flex flex-col gap-8 ${
          aside
            ? "lg:flex-row lg:items-end lg:justify-between lg:gap-16"
            : centered
            ? "items-center text-center"
            : ""
        }`}
      >
        <div className={centered && !aside ? "flex flex-col items-center" : ""}>
          <h2 className={`display ${aside ? "max-w-[16ch]" : "max-w-[20ch]"}`}>
            {title}
          </h2>
          {lede && (
            <p className={`lede mt-5 ${centered && !aside ? "text-center" : ""}`}>
              {lede}
            </p>
          )}
        </div>

        {aside && <div className="lg:pb-2 lg:shrink-0">{aside}</div>}
      </div>
    </motion.div>
  );
};

/** Small caps label with a leading gold tick. */
export const Label = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-2.5 label ${className}`}>
    <span aria-hidden className="w-6 h-px bg-gold" />
    {children}
  </span>
);

/** Shared reveal for list/grid items — one motion language across the site. */
export const reveal = (i = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay: Math.min(i, 6) * 0.06, ease: [0.22, 1, 0.36, 1] },
});

export default Section;
